import { useState, useEffect } from 'react';
import { collection, query, orderBy, onSnapshot, addDoc, updateDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { Patient } from '../types';
import { toast } from 'react-hot-toast';

export function usePatients() {
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const q = query(collection(db, 'patients'), orderBy('name'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const patientsData = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as Patient[];
      
      setPatients(patientsData);
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  const addPatient = async (patientData: Omit<Patient, 'id' | 'createdAt' | 'updatedAt'>) => {
    try {
      // Validar dados obrigatórios
      if (!patientData.name || !patientData.cpf || !patientData.birthDate) {
        throw new Error('Nome, CPF e data de nascimento são obrigatórios');
      }

      // Validar formato de data
      const birthDate = new Date(patientData.birthDate);
      if (isNaN(birthDate.getTime())) {
        throw new Error('Data de nascimento inválida');
      }

      // Função para remover campos undefined recursivamente
      const removeUndefinedFields = (obj: any): any => {
        if (obj === null || obj === undefined) {
          return null;
        }
        
        if (Array.isArray(obj)) {
          return obj.map(removeUndefinedFields);
        }
        
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              cleaned[key] = removeUndefinedFields(value);
            }
          }
          return cleaned;
        }
        
        return obj;
      };

      // Limpar dados antes de enviar
      const cleanedData = removeUndefinedFields(patientData);

      const now = new Date().toISOString();
      const docRef = await addDoc(collection(db, 'patients'), {
        ...cleanedData,
        createdAt: now,
        updatedAt: now
      });
      
      console.log('Paciente criado com ID:', docRef.id);
      toast.success('Paciente cadastrado com sucesso!');
    } catch (error) {
      console.error('Erro ao cadastrar paciente:', error);
      
      if (error instanceof Error) {
        toast.error(`Erro ao cadastrar paciente: ${error.message}`);
      } else {
        toast.error('Erro desconhecido ao cadastrar paciente');
      }
      throw error;
    }
  };

  const updatePatient = async (id: string, patientData: Partial<Patient>) => {
    try {
      // Validar se o ID existe
      if (!id) {
        throw new Error('ID do paciente é obrigatório para atualização');
      }

      // Função para remover campos undefined recursivamente
      const removeUndefinedFields = (obj: any): any => {
        if (obj === null || obj === undefined) {
          return null;
        }
        
        if (Array.isArray(obj)) {
          return obj.map(removeUndefinedFields);
        }
        
        if (typeof obj === 'object') {
          const cleaned: any = {};
          for (const [key, value] of Object.entries(obj)) {
            if (value !== undefined) {
              cleaned[key] = removeUndefinedFields(value);
            }
          }
          return cleaned;
        }
        
        return obj;
      };

      // Limpar dados antes de enviar
      const cleanedData = removeUndefinedFields(patientData);

      await updateDoc(doc(db, 'patients', id), {
        ...cleanedData,
        updatedAt: new Date().toISOString()
      });
      toast.success('Paciente atualizado com sucesso!');
    } catch (error) {
      console.error('Erro ao atualizar paciente:', error);
      
      if (error instanceof Error) {
        toast.error(`Erro ao atualizar paciente: ${error.message}`);
      } else {
        toast.error('Erro desconhecido ao atualizar paciente');
      }
      throw error;
    }
  };

  const deletePatient = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'patients', id));
      toast.success('Paciente removido com sucesso!');
    } catch (error) {
      console.error('Erro ao remover paciente:', error);
      toast.error('Erro ao remover paciente');
      throw error;
    }
  };

  const searchPatients = (searchTerm: string) => {
    if (!searchTerm) return patients;
    
    return patients.filter(patient =>
      patient.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      patient.cpf.includes(searchTerm) ||
      patient.cns.includes(searchTerm)
    );
  };

  return {
    patients,
    loading,
    addPatient,
    updatePatient,
    deletePatient,
    searchPatients
  };
}